import { useEffect, useState } from 'react';
import {
  chat,
  type Category,
  type ChatRoom as Room,
  type RoomState,
} from '../lib/api.ts';
import { onLive } from '../lib/live.ts';
import { ChatRoom } from './ChatRoom.tsx';

interface Props {
  readonly categories: readonly Category[];
}

/**
 * The site's way in.
 *
 * Category first, because that is what decides who hears it. Picking a
 * person would mean the site guessing who covers what, which is the
 * knowledge the category assignment exists to hold for them.
 */
export const SiteChat = ({ categories }: Props) => {
  const [room, setRoom] = useState<RoomState | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState('');
  const [live, setLive] = useState<Room[]>([]);

  // Existing conversations, so a staff reply is reachable. Without
  // this the badge said "1 waiting" and the only way in was guessing
  // which category it was in.
  const refresh = () => {
    chat
      .rooms()
      .then((r) => setLive(r.filter((x) => !x.closedAt)))
      .catch(() => setLive([]));
  };

  useEffect(() => {
    refresh();

    // The poll is the safety net for a dropped socket, not the
    // mechanism: a message from head office should land now, not in
    // up to fifteen seconds.
    const off = onLive((e) => {
      if (e.type === 'chat') refresh();
    });
    const id = window.setInterval(refresh, 15000);

    return () => {
      off();
      window.clearInterval(id);
    };
  }, []);

  const open = async (categoryId: string) => {
    setBusy(categoryId);
    try {
      setRoom(await chat.open(categoryId));
      setPicking(false);
    } catch {
      setBusy('');
    } finally {
      setBusy('');
    }
  };

  if (room) {
    return (
      <div className="noticeWrap">
        <button className="linkish" onClick={() => setRoom(null)}>
          Back to categories
        </button>
        <ChatRoom
          sessionId={room.sessionId}
          initial={room.messages}
          staffPresent={room.staffPresent}
          canSign
          onClose={() => {
            void chat.close(room.sessionId).catch(() => undefined);
            setRoom(null);
          }}
        />
      </div>
    );
  }

  if (!picking) {
    return (
      <>
      {live.length > 0 && (
        <div className="noticeWrap">
          <p className="mono">Open conversations</p>
          {live.map((r) => (
            <button
              key={r.id}
              className="sentRow"
              onClick={() => void open(r.categoryId)}
            >
              <span className="sentBody">
                <span className="catTag" style={{ background: r.color }} />
                {/* A conversation with head office is with a
                    person. The category is how it was filed, which
                    is our concern and not the counter's. */}
                {r.withName ?? r.categoryName}
              </span>
              <span className="mono sentCount">
                {r.unread > 0 && (
                  <b className="unreadTag">{r.unread}</b>
                )}
                <span className="sentGo">Open</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <button className="chatOpen" onClick={() => setPicking(true)}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 15 a2 2 0 0 1-2 2 H8 l-4 3 V6 a2 2 0 0 1 2-2 h12 a2 2 0 0 1 2 2 z" />
        </svg>
        Ask someone now
      </button>
      </>
    );
  }

  return (
    <div className="noticeWrap">
      <p className="mono">Who do you need?</p>
      <div className="catRail">
        {categories.map((c) => (
          <button
            key={c.id}
            className="cat"
            disabled={busy === c.id}
            onClick={() => void open(c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>
      <button className="linkish" onClick={() => setPicking(false)}>
        Cancel
      </button>
    </div>
  );
};
